import { useQuery } from '@tanstack/react-query'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import FbButton from 'components/FbUI/FbButton'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import PipelineExportModal from 'components/Modals/ExportModal/PipelineExportModal'
import { useMemo, useState } from 'react'
import { BsDownload } from 'react-icons/bs'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import styled from 'styled-components'
import { usePaginationURLParams } from 'utils/usePaginationURLParams'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { FBPageTableContainer } from '../../FbUI/Page/styles'
import DealsPipelineTableFilters from './DealsPipelineFilterset'
import { useDealsPipelineTableColumns } from './useDealsPipelineTableColumns'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { TableSearchWithElastic } from 'components/Filters/TableSearchWithElastic'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { cn } from 'components/UI/cn'

// Default columns: Company, Deals, Sales Stages, Last Update, Annual Revenue Opportunity, Annual Volume Opportunity
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  closed: false,
  taro_closed: false,
  days_since_last_modified: false,
  annual_volume_closed: false,
}

interface DealsPipelineTableProps {
  tableKey: string
}

const PAGE_SIZE = 20
const IDENTIFIER = 'DEALS_PIPELINE_TABLE'

function DealsPipelineTableComponent(props: DealsPipelineTableProps) {
  const { enabledReportDeals } = useFeatureFlagService()
  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const {
    methods: { clearSelectedRows },
  } = useDataTableContext()
  const api = useApiService()
  const dealsPipelineFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filtersAsParams = useFilterParams(dealsPipelineFilterStore)
  const [pagination] = usePaginationURLParams(PAGE_SIZE)
  const [showExportModal, setShowExportModal] = useState(false)
  const filterParams = useMemo(() => {
    const params: Record<string, any> = filtersAsParams

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting, filtersAsParams])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterParams,
    page: pagination.pageIndex,
  })

  // DATA FETCHING
  const { isFetching, data } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      const fullParams = {
        ...filterParams,
        page: pagination.pageIndex + 1,
        has_stage: true,
      }

      if (enabledReportDeals) {
        return await api.getDealsPipelineV2(fullParams, signal)
      }

      return await api.getDealsPipeline(fullParams, signal)
    },
  })

  const columns = useDealsPipelineTableColumns()

  return (
    <>
      <h2 className="uppercase font-light text-2xl text-gray-700">
        Companies in Pipeline
      </h2>
      <hr className="border-gray-400 mb-6 mt-2" />

      <FBPageTableContainer style={{ height: '800px' }}>
        <DataTable
          tableHeader={
            <>
              <SearchContainer>
                <div className="flex w-full gap-2 items-center">
                  <TableSearchWithElastic
                    filterStore={dealsPipelineFilterStore}
                    historyKey={props.tableKey}
                    options={[
                      {
                        label: 'Name',
                        searchKey: '',
                        includeKeyInValue: false,
                        paramKey: 'search',
                        searchPlaceholder: 'Type chain name',
                        elastic: true,
                      },
                    ]}
                  />
                  <DealsPipelineTableFilters identifier={IDENTIFIER} />

                  <ColumnSelectorRecipient tableKey={props.tableKey} />
                </div>

                <div className={cn('flex flex-row justify-between gap-4')}>
                  <FilterChips
                    identifier={IDENTIFIER}
                    store={dealsPipelineFilterStore}
                    showActive
                    clearAllButton
                  />
                </div>
              </SearchContainer>
            </>
          }
          tableKey={props.tableKey}
          loading={isFetching}
          data={data ?? []}
          columns={columns}
          sortableFields="all"
          virtualizeRows
          enableRowSelection={false}
          defaultSort={[{ id: 'last_modified' as any, desc: true }]}
          footerControls={
            <ExportButton
              variant="secondary"
              onClick={() => setShowExportModal(true)}
            >
              <BsDownload size={18} />
            </ExportButton>
          }
        />
      </FBPageTableContainer>

      <PipelineExportModal
        show={showExportModal}
        handleClose={() => setShowExportModal(false)}
        filters={filterParams}
      />
    </>
  )
}

export function DealsPipelineTable(props: DealsPipelineTableProps) {
  const { tableKey, ...rest } = props

  return (
    <ColumnsStoreProvider
      savedPresetsIdentifier={'DEALS_PIPELINE_TABLE'}
      tableKey={tableKey}
      defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
    >
      <DataTableProvider tableKey={tableKey}>
        <DealsPipelineTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`

const ExportButton = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
